import { FirebaseError } from 'firebase/app';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onIdTokenChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  type User,
} from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { firebaseConfigured } from '../lib/env';
import { auth } from '../lib/firebase';

/**
 * Firebase Auth, wrapped so the rest of the app never imports it directly.
 * Authorization is not decided here: the API resolves roles from Postgres on
 * every request. This only answers "who is holding this browser".
 */

export interface AuthContextValue {
  user: User | null;
  /** True until Firebase has restored (or ruled out) a persisted session. */
  loading: boolean;
  configured: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (name: string, email: string, password: string) => Promise<void>;
  sendReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Firebase's error codes are not sentences. These are. */
export function authErrorMessage(error: unknown): string {
  const code = error instanceof FirebaseError ? error.code : '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That does not look like an email address.';
    case 'auth/missing-password':
      return 'Enter your password.';
    case 'auth/weak-password':
      return 'Passwords need to be at least six characters.';
    case 'auth/email-already-in-use':
      return 'There is already an account with that email. Sign in instead.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'That email and password do not match an account.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a minute and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'The Google sign-in window closed before finishing.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the Google sign-in window. Allow popups for this site and try again.';
    case 'auth/network-request-failed':
      return 'Could not reach the sign-in service. Check your connection.';
    case 'auth/operation-not-allowed':
      return 'That sign-in method is not enabled for this project yet.';
    default:
      if (error instanceof Error && error.message) return error.message;
      return 'Sign in failed. Try again.';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(firebaseConfigured);

  useEffect(() => {
    if (!firebaseConfigured) return;
    // onIdTokenChanged rather than onAuthStateChanged: it also fires on token
    // refresh, which keeps `user.getIdToken()` cheap for the API client.
    return onIdTokenChanged(auth, (next) => {
      // Anonymous sessions belong to the guest app, never to this one; if one
      // somehow lands here it is not a signed-in Slate user.
      setUser(next && !next.isAnonymous ? next : null);
      setLoading(false);
    });
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    await signInWithPopup(auth, provider);
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email.trim(), password);
  }, []);

  const signUpWithEmail = useCallback(async (name: string, email: string, password: string) => {
    const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
    const displayName = name.trim();
    if (displayName) {
      await updateProfile(credential.user, { displayName });
      // The API reads `name` from the ID token, which only picks up the new
      // profile after a forced refresh.
      await credential.user.getIdToken(true);
      setUser(auth.currentUser);
    }
  }, []);

  const sendReset = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email.trim());
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      configured: firebaseConfigured,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      sendReset,
      signOut,
    }),
    [loading, sendReset, signInWithEmail, signInWithGoogle, signOut, signUpWithEmail, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>.');
  return value;
}

/** Best available human name for the signed-in user. */
export function displayNameOf(user: User | null): string {
  if (!user) return '';
  return user.displayName?.trim() || user.email?.split('@')[0] || 'You';
}

/** "Garrett's Organization": the default name, from whoever is signing up. */
export function defaultOrganizationName(user: User | null): string {
  const first = displayNameOf(user).split(' ')[0];
  if (!first || first === 'You') return 'My Organization';
  return `${first}${first.endsWith('s') ? "'" : "'s"} Organization`;
}

